import { supabase } from './client';
import type {
  MarketingTaskRow,
  MarketingTaskInsert,
  MarketingTaskUpdate,
  MarketingTaskType,
  MarketingTaskStatus,
} from './database.types';

// ============================================
// Frontend Data Interface (camelCase)
// ============================================

export interface MarketingTaskData {
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  description: string | null;
  taskType: MarketingTaskType;
  status: MarketingTaskStatus;
  result: Record<string, unknown> | null;
  triggeredBy: 'user' | 'agent' | 'scheduled';
  completedAt: string | null;
}

// ============================================
// Mapping Helpers
// ============================================

const mapRow = (row: MarketingTaskRow): MarketingTaskData => ({
  id: row.id,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  title: row.title,
  description: row.description,
  taskType: row.task_type,
  status: row.status,
  result: row.result as Record<string, unknown> | null,
  triggeredBy: row.triggered_by,
  completedAt: row.completed_at,
});

// ============================================
// Marketing Tasks API
// ============================================

class MarketingTasksApi {
  async getAll(params?: {
    status?: MarketingTaskStatus;
    taskType?: MarketingTaskType;
    limit?: number;
  }): Promise<MarketingTaskData[]> {
    let query = supabase
      .from('marketing_tasks')
      .select('*')
      .order('created_at', { ascending: false });

    if (params?.status) {
      query = query.eq('status', params.status);
    }
    if (params?.taskType) {
      query = query.eq('task_type', params.taskType);
    }
    if (params?.limit) {
      query = query.limit(params.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map(mapRow);
  }

  async create(task: Omit<MarketingTaskInsert, 'id' | 'created_at' | 'updated_at'>): Promise<MarketingTaskData> {
    const { data, error } = await supabase
      .from('marketing_tasks')
      .insert(task)
      .select()
      .single();

    if (error) throw error;
    return mapRow(data);
  }

  async update(id: string, updates: MarketingTaskUpdate): Promise<MarketingTaskData> {
    const { data, error } = await supabase
      .from('marketing_tasks')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return mapRow(data);
  }

  async complete(id: string, result?: Record<string, unknown>): Promise<MarketingTaskData> {
    return this.update(id, {
      status: 'completed',
      completed_at: new Date().toISOString(),
      result: result as any,
    });
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('marketing_tasks')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async getStats(): Promise<{
    total: number;
    completed: number;
    pending: number;
    failed: number;
    byType: Record<string, number>;
  }> {
    const { data, error } = await supabase
      .from('marketing_tasks')
      .select('status, task_type');

    if (error) throw error;

    const tasks = data || [];
    const byType: Record<string, number> = {};

    for (const t of tasks) {
      byType[t.task_type] = (byType[t.task_type] || 0) + 1;
    }

    return {
      total: tasks.length,
      completed: tasks.filter(t => t.status === 'completed').length,
      pending: tasks.filter(t => t.status === 'pending' || t.status === 'in_progress').length,
      failed: tasks.filter(t => t.status === 'failed').length,
      byType,
    };
  }
}

export const marketingTasksApi = new MarketingTasksApi();
